import { useEffect, useRef, useState } from 'react'
import BottomSheet from '../BottomSheet'
import Text from '../Text'
import CloseFilledIcon from '../Icons/CloseFilledIcon'
import {
  MYSAATHI_VARIANT_EVENT,
  MYSAATHI_VARIANT_STORAGE_KEY,
} from '../../constants/sema-constants'
import './ProgramOfferingSection.css'

const OfferingRow = ({ item, onClick }) => {
  const hasReplaceImage =
    item?.title?.includes('replaceImage') && item?.replaceImage

  const cleanedTitle = hasReplaceImage
    ? item?.title.replace('replaceImage', '').trim()
    : item?.title

  const isClickable = Boolean(onClick)

  return (
    <div
      className={`flex items-center py-3 border-b border-[#E4E7EC] last:border-b-0 last:pb-1 px-2 ${
        isClickable ? 'justify-between cursor-pointer' : ''
      }`}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
    >
      <div className="flex items-start gap-3 flex-1">
        <div className="w-3.5 h-3.5 rounded-full bg-[#A5508F] flex-shrink-0 mt-[3px]" />
        <div className="flex items-center gap-x-2 flex-wrap">
          {hasReplaceImage ? (
            <img
              src={item?.replaceImage}
              alt=""
              className="h-6 w-auto object-contain"
            />
          ) : null}
          <Text
            tag="span"
            variant="body"
            size="medium"
            className="text-primary-text"
          >
            {cleanedTitle}
          </Text>
          {item?.subText ? (
            <Text
              tag="span"
              variant="body"
              size="small"
              className="text-[#98A2B3] pt-[2px]"
            >
              {item.subText}
            </Text>
          ) : null}
        </div>
      </div>
      {isClickable ? (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="text-gray-400 flex-shrink-0"
        >
          <path
            d="M7.5 15L12.5 10L7.5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </div>
  )
}

const ProgramCard = ({ program, onOfferingClick }) => {
  const welcomeKit = program?.welcomeKit
  const welcomeItems = Array.isArray(welcomeKit?.items) ? welcomeKit.items : []

  return (
    <div
      className="rounded-2xl overflow-hidden p-2"
      style={{
        backgroundColor: program?.backgroundColor ?? '#E4F2F2',
      }}
    >
      <div
        className="p-3 rounded-xl"
        style={{
          backgroundColor: program?.head?.backgroundColor ?? '#A5508F',
        }}
      >
        <Text
          tag="p"
          variant="headline"
          size="small"
          className="text-white font-bold !text-[22px] leading-[32px]"
        >
          {program?.head?.title}
        </Text>
        <Text
          tag="p"
          variant="body"
          size="medium"
          className="text-white opacity-[0.6]"
        >
          {program?.head?.subTitle}
        </Text>
      </div>

      <div className="pb-0">
        {program?.offerings?.map((offering, offeringIndex) => (
          <OfferingRow
            key={`offering-${offeringIndex}`}
            item={offering}
            onClick={
              offering?.bottomSheet
                ? () => onOfferingClick(offering)
                : undefined
            }
          />
        ))}

        {welcomeKit?.title && welcomeItems.length ? (
          <>
            <div className="px-2 pt-3 pb-1">
              <Text
                tag="p"
                variant="label"
                size="small"
                className="text-[#98A2B3] mspo-group"
              >
                {welcomeKit.title}
              </Text>
            </div>
            {welcomeItems.map((item, itemIndex) => (
              <OfferingRow key={`welcome-${itemIndex}`} item={item} />
            ))}
          </>
        ) : null}
      </div>
    </div>
  )
}

const ProgramOfferingSection = ({ data }) => {
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const [selectedCard, setSelectedCard] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const touchStartX = useRef(null)
  const title = data?.heading
  const description = data?.description
  const image = data?.image
  const cards = Array.isArray(data?.programs) ? data.programs : []

  const handleCardClick = (offering) => {
    setSelectedCard(offering?.bottomSheet)
    setShowBottomSheet(true)
  }

  const handleBottomSheetClose = () => {
    setShowBottomSheet(false)
    setSelectedCard(null)
  }

  const goTo = (index) => {
    if (!cards.length) return
    const next = Math.max(0, Math.min(index, cards.length - 1))
    setActiveIndex(next)
  }

  const goToVariant = (variantId, { scrollIntoView = false } = {}) => {
    if (!variantId || !cards.length) return
    const index = cards.findIndex((program) => program?.variant === variantId)
    if (index < 0) return
    goTo(index)
    if (scrollIntoView) {
      document
        .getElementById('mspo')
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  useEffect(() => {
    try {
      const stored = localStorage.getItem(MYSAATHI_VARIANT_STORAGE_KEY)
      if (stored) goToVariant(stored)
    } catch (_) {
      // ignore storage failures
    }

    const onVariantSelected = (event) => {
      goToVariant(event?.detail?.variant, { scrollIntoView: true })
    }
    window.addEventListener(MYSAATHI_VARIANT_EVENT, onVariantSelected)
    return () => {
      window.removeEventListener(MYSAATHI_VARIANT_EVENT, onVariantSelected)
    }
    // cards identity is stable per landing payload; re-bind when programs change
  }, [cards])

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null
  }

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return
    const endX = e.changedTouches?.[0]?.clientX
    if (endX == null) return
    const delta = endX - touchStartX.current
    touchStartX.current = null
    if (Math.abs(delta) < 40) return
    if (delta < 0) goTo(activeIndex + 1)
    else goTo(activeIndex - 1)
  }

  return (
    <section className="w-full bg-white">
      <div className="max-w-[1024px] mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="space-y-3">
            {title ? (
              <Text
                variant="headline"
                size="small"
                tag="p"
                className="text-primary-text"
              >
                {title}
              </Text>
            ) : null}
            {description ? (
              <div className="text-[16px] text-[#475569]">{description}</div>
            ) : null}

            {cards.length ? (
              <div className="pt-2 space-y-3">
                <div className="mspo" id="mspo">
                  {cards.length > 1 ? (
                    <div
                      className="mspo-tabs"
                      role="tablist"
                      aria-label="MySaathi variant"
                    >
                      {cards.map((program, index) => {
                        const selected = index === activeIndex
                        const activeTabColor = program?.tabColor ?? '#189395'

                        return (
                          <button
                            key={`mspo-tab-${index}`}
                            type="button"
                            className="mspo-tab"
                            role="tab"
                            aria-selected={selected}
                            data-i={index}
                            id={`mspo-tab-${index}`}
                            aria-controls={`mspo-panel-${index}`}
                            onClick={() => goTo(index)}
                            style={
                              selected
                                ? {
                                    backgroundColor: activeTabColor,
                                    color: '#ffffff',
                                  }
                                : undefined
                            }
                          >
                            {program?.tabTitle}
                          </button>
                        )
                      })}
                    </div>
                  ) : null}

                  <div
                    className="mspo-track"
                    id="mspo-track"
                    style={{
                      transform: `translateX(-${activeIndex * 100}%)`,
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                  >
                    {cards.map((program, index) => (
                      <div
                        key={`mspo-panel-${index}`}
                        className="mspo-panel"
                        id={`mspo-panel-${index}`}
                        role="tabpanel"
                        aria-labelledby={`mspo-tab-${index}`}
                        aria-hidden={index !== activeIndex}
                      >
                        <ProgramCard
                          program={program}
                          onOfferingClick={handleCardClick}
                        />
                      </div>
                    ))}
                  </div>

                  {cards.length > 1 ? (
                    <div className="mspo-dots" aria-hidden="true">
                      {cards.map((_, index) => (
                        <span
                          key={`mspo-dot-${index}`}
                          className="mspo-dot"
                          data-i={index}
                          data-active={index === activeIndex}
                          onClick={() => goTo(index)}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          {image ? (
            <div>
              <img
                src={image}
                alt={title || 'Premium care'}
                className="w-full max-w-[420px] mx-auto object-contain"
              />
            </div>
          ) : null}
        </div>
      </div>

      <BottomSheet
        show={showBottomSheet}
        onClose={handleBottomSheetClose}
        title={selectedCard?.title}
        closeIcon={<CloseFilledIcon />}
        headerClassName="!px-3.5 !py-2"
      >
        <div className="p-4">
          <div className="space-y-4 bg-[#FFFBEE] p-2 rounded-lg">
            <Text variant="body" size="medium" tag="p" className="capitalize">
              {selectedCard?.description}
            </Text>
            <div className="flex items-center gap-2">
              {selectedCard?.stripText ? (
                <div className="bg-[linear-gradient(100deg,_#826E2D_2.41%,_#A68A2D_46.33%,_#826E2D_100%)] px-1.5 py-1 rounded-lg">
                  <Text
                    variant="body"
                    size="medium"
                    tag="p"
                    className="capitalize text-white"
                  >
                    {selectedCard?.stripText}
                  </Text>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </BottomSheet>
    </section>
  )
}

export default ProgramOfferingSection
